import { useState } from 'react'
import { Button, Card } from '@/components/ui'
import { api } from '@/services/api'
import { env } from '@/services/env'
import type { PlanTier } from '@/services/types'

interface PlanUpgradeWizardProps {
  currentPlan: PlanTier
  onCancel: () => void
}

type WizardStep = 'select' | 'confirm'

const plans: {
  tier: Exclude<PlanTier, 'free'>
  name: string
  price: number
  description: string
  features: string[]
  highlighted: boolean
}[] = [
  {
    tier: 'solo',
    name: 'Solo',
    price: 9,
    description: 'For individuals getting started',
    features: [
      '50 generations/month',
      'Voice analysis & AI generation',
      'Content queue & editing',
      'Direct posting to X',
    ],
    highlighted: false,
  },
  {
    tier: 'pro',
    name: 'Pro',
    price: 29,
    description: 'For creators who mean business',
    features: [
      '200 generations/month',
      'Everything in Solo',
      'Post scheduling & auto-posting',
      'Analytics dashboard',
    ],
    highlighted: true,
  },
  {
    tier: 'agency',
    name: 'Agency',
    price: 79,
    description: 'For teams and power users',
    features: [
      'Unlimited generations',
      'Everything in Pro',
      'Multiple X accounts',
      'Priority support',
    ],
    highlighted: false,
  },
]

export function PlanUpgradeWizard({ currentPlan, onCancel }: PlanUpgradeWizardProps) {
  const [step, setStep] = useState<WizardStep>('select')
  const [selectedTier, setSelectedTier] = useState<Exclude<PlanTier, 'free'> | null>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedPlan = plans.find((p) => p.tier === selectedTier)

  const handleSelectPlan = (tier: Exclude<PlanTier, 'free'>) => {
    setSelectedTier(tier)
    setError(null)
    setStep('confirm')
  }

  const handleConfirm = async () => {
    if (!selectedTier) return
    setIsRedirecting(true)
    setError(null)
    try {
      const frontendUrl = env.frontendUrl
      const response = await api.createCheckoutSession(
        selectedTier,
        `${frontendUrl}/checkout/success`,
        `${frontendUrl}/checkout/cancel`
      )
      window.location.href = response.checkout_url
    } catch {
      setError('Failed to start checkout. Please try again.')
      setIsRedirecting(false)
    }
  }

  const stepIndicator = (
    <div className="flex items-center gap-3 mb-6 text-sm">
      <span className={step === 'select' ? 'font-medium' : 'text-secondary-text'}>
        1. Choose plan
      </span>
      <span className="text-secondary-text">/</span>
      <span className={step === 'confirm' ? 'font-medium' : 'text-secondary-text'}>
        2. Confirm & checkout
      </span>
    </div>
  )

  if (step === 'confirm' && selectedPlan) {
    return (
      <div>
        {stepIndicator}

        <Card>
          <h3 className="font-medium mb-4">Confirm upgrade</h3>

          <div className="border border-border p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-lg font-medium">{selectedPlan.name}</span>
              <span>
                <span className="text-2xl font-medium">${selectedPlan.price}</span>
                <span className="text-secondary-text text-sm">/mo</span>
              </span>
            </div>
            <p className="text-sm text-secondary-text mb-3">{selectedPlan.description}</p>
            <ul className="space-y-1.5">
              {selectedPlan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <span className="text-primary-text mt-0.5">&#10003;</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {currentPlan === 'free' ? (
            <p className="text-sm text-secondary-text mb-4">
              You're upgrading from the free tier to <span className="font-medium">{selectedPlan.name}</span>.
              Your generation counter will reset and you'll get {selectedPlan.tier === 'agency' ? 'unlimited' : selectedPlan.features[0].toLowerCase().replace(' generations/month', '')} generations each month.
            </p>
          ) : (
            <p className="text-sm text-secondary-text mb-4">
              You're switching from {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} to <span className="font-medium">{selectedPlan.name}</span>.
              Changes take effect at the start of your next billing period.
            </p>
          )}

          {error && (
            <p className="text-sm text-error mb-4">{error}</p>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={handleConfirm} loading={isRedirecting}>
              Continue to checkout
            </Button>
            <Button
              variant="secondary"
              onClick={() => { setStep('select'); setError(null) }}
              disabled={isRedirecting}
            >
              Back
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div>
      {stepIndicator}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {plans.map((plan) => {
          const isCurrent = plan.tier === currentPlan
          return (
            <div
              key={plan.tier}
              className={`border p-4 flex flex-col ${
                plan.highlighted && !isCurrent
                  ? 'border-primary-text border-2'
                  : isCurrent
                    ? 'border-primary-text border-2 bg-secondary-bg/50'
                    : 'border-border'
              }`}
            >
              {isCurrent && (
                <span className="text-xs font-medium bg-primary-text text-primary-bg px-2 py-0.5 self-start mb-3">
                  Current plan
                </span>
              )}
              {!isCurrent && plan.highlighted && (
                <span className="text-xs font-medium bg-primary-text text-primary-bg px-2 py-0.5 self-start mb-3">
                  Most Popular
                </span>
              )}
              <h3 className="text-lg font-medium">{plan.name}</h3>
              <p className="text-secondary-text text-sm mt-1">{plan.description}</p>
              <div className="mt-3 mb-4">
                <span className="text-3xl font-medium">${plan.price}</span>
                <span className="text-secondary-text text-sm">/mo</span>
              </div>
              <ul className="space-y-2 mb-4 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <span className="text-primary-text mt-0.5">&#10003;</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <Button variant="secondary" disabled>
                  Current plan
                </Button>
              ) : (
                <Button
                  variant={plan.highlighted ? 'primary' : 'secondary'}
                  onClick={() => handleSelectPlan(plan.tier)}
                >
                  Select {plan.name}
                </Button>
              )}
            </div>
          )
        })}
      </div>

      <button
        onClick={onCancel}
        className="text-sm text-secondary-text hover:text-primary-text transition-colors"
      >
        Cancel
      </button>
    </div>
  )
}
